import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Shelter } from "@/types/shelter";

// Lekki select – pomijamy ciężkie kolumny tekstowe (description, volunteer_info),
// które są używane wyłącznie na stronie szczegółów schroniska.
// Te pola dociąga osobno useShelterDetails(id).
const LIGHT_COLUMNS =
  "id,slug,name,city,region,address,postal_code,lat,lng,phone,email,website," +
  "current_needs,urgent_needs,urgent_needs_set_at,dog_walking_available," +
  "volunteering_available,dogs_available,cats_available,last_updated,created_at," +
  "data_verified,organization_verified,organization_verified_at," +
  "organization_verification_pending,organization_verification_requested_at,contacted,has_owner,is_test";

// Timeout dla pojedynczej próby zapytania do Supabase.
const QUERY_TIMEOUT_MS = 10_000;

const withTimeout = <T,>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Przekroczono czas oczekiwania (${label})`));
    }, ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
};

export const useShelters = () => {
  return useQuery({
    queryKey: ["shelters"],
    // Ponów do 3 razy z rosnącym opóźnieniem (1s, 2s, 4s, max 10s)
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
    queryFn: async (): Promise<Shelter[]> => {
      const { data, error } = await withTimeout(
        supabase.from("shelters").select(LIGHT_COLUMNS).order("name"),
        QUERY_TIMEOUT_MS,
        "lista schronisk",
      );

      if (error) {
        throw new Error(error.message);
      }

      return (data || []).map((shelter: any) => ({
        id: shelter.id,
        slug: shelter.slug,
        name: shelter.name,
        city: shelter.city,
        region: shelter.region,
        address: shelter.address,
        postal_code: shelter.postal_code ?? null,
        lat: shelter.lat,
        lng: shelter.lng,
        phone: shelter.phone,
        email: shelter.email,
        website: shelter.website,
        current_needs: shelter.current_needs,
        urgent_needs: shelter.urgent_needs,
        urgent_needs_set_at: shelter.urgent_needs_set_at,
        dog_walking_available: shelter.dog_walking_available,
        volunteering_available: shelter.volunteering_available,
        dogs_available: shelter.dogs_available,
        cats_available: shelter.cats_available,
        // Pola pomijane w light select – dostępne przez useShelterDetails
        volunteer_info: null,
        description: null,
        last_updated: shelter.last_updated ?? shelter.created_at,
        created_at: shelter.created_at,
        data_verified: shelter.data_verified ?? false,
        organization_verified: shelter.organization_verified ?? false,
        organization_verified_at: shelter.organization_verified_at ?? null,
        organization_verification_pending: shelter.organization_verification_pending ?? false,
        organization_verification_requested_at: shelter.organization_verification_requested_at ?? null,
        contacted: shelter.contacted ?? false,
        has_owner: shelter.has_owner ?? false,
        is_test: shelter.is_test ?? false,
      }));
    },
  });
};

// Pełne dane konkretnego schroniska (z description i volunteer_info).
// Używane na stronie szczegółów /schronisko/:slug oraz w komponencie ShelterDetail.
export const useShelterDetails = (id: string | null | undefined) => {
  return useQuery({
    queryKey: ["shelter-details", id],
    enabled: !!id,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
    queryFn: async () => {
      const { data, error } = await withTimeout(
        supabase.from("shelters").select("description,volunteer_info").eq("id", id!).maybeSingle(),
        QUERY_TIMEOUT_MS,
        "szczegóły schroniska",
      );
      if (error) throw new Error(error.message);
      return data as { description: string | null; volunteer_info: string | null } | null;
    },
  });
};
